import { useEffect, useState } from "react";
import { brand } from "../theme/brand.config";

// The MCP endpoint shown in agent-setup instructions. Read at runtime
// from the worker's RFC 9728 Protected Resource Metadata document so
// the worker's MCP_RESOURCE_URL stays the single source of truth — a
// build-time copy in the SPA could drift and instruct users to connect
// to a URL whose OAuth token audience (RFC 8707) the worker rejects.
// Memoized module-level so repeated mounts share one request, same
// discipline as useConnectedCount.
let cache: Promise<string> | null = null;

function load(): Promise<string> {
  if (!cache) {
    cache = fetch(`${brand.apiBaseUrl}/.well-known/oauth-protected-resource`)
      .then((r) =>
        r.ok
          ? (r.json() as Promise<{ resource?: unknown }>)
          : Promise.reject(new Error(`http_${r.status}`)),
      )
      .then((j) =>
        typeof j.resource === "string" && j.resource.length > 0
          ? j.resource
          : Promise.reject(new Error("prm_missing_resource")),
      )
      .catch(() => {
        // Drop the memo so a later mount retries, and fall back to the
        // dev-shaped URL. If the worker is unreachable the rest of the
        // portal is broken too, so this fallback is rarely user-visible.
        cache = null;
        return `${brand.apiBaseUrl}/mcp`;
      });
  }
  return cache;
}

// null until resolved — render a placeholder, not the fallback, so a
// slow fetch never flashes a wrong URL in front of the right one.
export function useMcpUrl(): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void load().then((u) => {
      if (alive) setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, []);
  return url;
}
